// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { createSelector } from 'reselect'

import { fullSystemStateSelector } from './fullFileFirstOrder'
import { EDITING, SEARCHING } from '../constants/editStates'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').FileURL} FileURL
 * @typedef {import('../index').ApplicationState} ApplicationState
 * @typedef {import('../index').ProjectApplicationState} ProjectApplicationState
 * @typedef {import('../index').SettingsApplicationState} SettingsApplicationState
 * @typedef {import('../index').FileApplicationState} FileApplicationState
 * @typedef {import('../index').SessionApplicationState} SessionApplicationState
 * @typedef {import('../index').LicenseApplicationState} LicenseApplicationState
 * @typedef {import('../index').SettingsWizardApplicationState} SettingsWizardApplicationState
 * @typedef {import('../index').ProOnboardingApplicationState} ProOnboardingApplicationState
 * @typedef {import('../index').UpdateApplicationState} UpdateApplicationState
 * @typedef {import('../index').WorkApplicationState} WorkApplicationState
 * @typedef {import('../index').UserInteractionsApplicationState} UserInteractionsApplicationState
 * @typedef {import('../index').DashboardApplicationState} DashboardApplicationState
 * @typedef {import('../index').BootingApplicationState} BootingApplicationState
 * @typedef {import('../index').ShutdownApplicationState} ShutdownApplicationState
 * @typedef {import('../index').UpdateInfo} UpdateInfo
 * @typedef {import('plottr_locales').Locale} Locale
 */

/**
 * @type {function(PlottrState): ApplicationState}
 */
export const applicationStateSelector = createSelector(fullSystemStateSelector, (state) => {
  return state.applicationState || {}
})

/**
 * @type {function(PlottrState): ProjectApplicationState}
 */
export const projectStateSelector = createSelector(
  applicationStateSelector,
  ({ project }) => project
)

/**
 * @type {function(PlottrState): boolean}
 */
export const plottrFileListIsLoadingSelector = createSelector(
  projectStateSelector,
  ({ loadingPlottrFileList }) => loadingPlottrFileList
)
/**
 * @type {function(PlottrState): boolean}
 */
export const proFileListIsLoadingSelector = createSelector(
  projectStateSelector,
  ({ loadingProFileList }) => loadingProFileList
)

/**
 * @type {function(PlottrState): boolean}
 */
export const importingNewProjectSelector = createSelector(
  projectStateSelector,
  ({ isImportingAProject }) => isImportingAProject
)

/**
 * @type {function(PlottrState): SettingsApplicationState}
 */
export const applicationSettingsSelector = createSelector(
  applicationStateSelector,
  ({ settings }) => settings
)
/**
 * @type {function(PlottrState): boolean}
 */
export const applicationSettingsAreLoadedSelector = createSelector(
  applicationSettingsSelector,
  ({ settingsLoaded }) => settingsLoaded
)

/**
 * @type {function(PlottrState): FileApplicationState}
 */
export const fileStateSelector = createSelector(applicationStateSelector, ({ file }) => file)
/**
 * @type {function(PlottrState): Boolean}
 */
export const checkingFileToLoadSelector = createSelector(
  fileStateSelector,
  ({ checkingFileToLoad }) => checkingFileToLoad
)
/**
 * @type {function(PlottrState): Boolean}
 */
export const checkedFileToLoadSelector = createSelector(
  fileStateSelector,
  ({ checkedFileToLoad }) => checkedFileToLoad
)
/**
 * @type {function(PlottrState): Boolean}
 */
export const fileIsLoadedSelector = createSelector(
  fileStateSelector,
  ({ fileLoaded }) => fileLoaded
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isRenamingFileSelector = createSelector(
  fileStateSelector,
  ({ renamingFile }) => renamingFile
)
/**
 * @type {function(PlottrState): boolean}
 */
export const creatingCloudFileSelector = createSelector(
  fileStateSelector,
  ({ creatingCloudFile }) => creatingCloudFile
)
/**
 * @type {function(PlottrState): boolean}
 */
export const uploadingFileToCloudSelector = createSelector(
  fileStateSelector,
  ({ uploadingFileToCloud }) => uploadingFileToCloud
)
/**
 * @type {function(PlottrState): boolean}
 */
export const loadingFileSelector = createSelector(
  fileStateSelector,
  ({ loadingFile }) => loadingFile
)
/**
 * @type {function(PlottrState): boolean}
 */
export const errorLoadingFileSelector = createSelector(
  fileStateSelector,
  ({ errorLoadingFile }) => !!errorLoadingFile
)
/**
 * @type {function(PlottrState): boolean}
 */
export const errorIsUpdateErrorSelector = createSelector(
  fileStateSelector,
  ({ errorIsUpdateError }) => !!errorIsUpdateError
)
/**
 * @type {function(PlottrState): boolean}
 */
export const deletingFileSelector = createSelector(
  fileStateSelector,
  ({ deletingFile }) => deletingFile
)
/**
 * @type {function(PlottrState): boolean}
 */
export const savingFileAsSelector = createSelector(
  fileStateSelector,
  ({ savingFileAs }) => savingFileAs
)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const filePathToUploadSelector = createSelector(
  fileStateSelector,
  ({ filePathToUpload }) => filePathToUpload
)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const filePathToProjectDuringBootSelector = createSelector(
  fileStateSelector,
  ({ pathToProjectDuringBoot }) => {
    return pathToProjectDuringBoot
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const noFileToShowSelector = createSelector(fileStateSelector, ({ noFileToShow }) => {
  return noFileToShow
})

/**
 * @type {function(PlottrState): (FileURL | Null)}
 */
export const fileURLToBootSelector = createSelector(fileStateSelector, ({ fileURLToBoot }) => {
  return fileURLToBoot
})

/**
 * @type {function(PlottrState): SessionApplicationState}
 */
export const sessionStateSelector = createSelector(
  applicationStateSelector,
  ({ session }) => session
)
/**
 * @type {function(PlottrState): boolean}
 */
export const sessionCheckedSelector = createSelector(
  sessionStateSelector,
  ({ sessionChecked }) => sessionChecked
)
/**
 * @type {function(PlottrState): boolean}
 */
export const checkingSessionSelector = createSelector(
  sessionStateSelector,
  ({ checkingSession }) => checkingSession
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isLoggingInSelector = createSelector(
  sessionStateSelector,
  ({ loggingIn }) => loggingIn
)

/**
 * @type {function(PlottrState): LicenseApplicationState}
 */
export const licenseStateSelector = createSelector(
  applicationStateSelector,
  ({ license }) => license
)
/**
 * @type {function(PlottrState): boolean}
 */
export const checkedTrialSelector = createSelector(
  licenseStateSelector,
  ({ trialLoaded }) => trialLoaded
)
export const trialLoadedSelector = checkedTrialSelector
/**
 * @type {function(PlottrState): boolean}
 */
export const checkingTrialSelector = createSelector(
  licenseStateSelector,
  ({ loadingTrial }) => loadingTrial
)
/**
 * @type {function(PlottrState): boolean}
 */
export const checkedLicenseSelector = createSelector(
  licenseStateSelector,
  ({ licenseLoaded }) => licenseLoaded
)
export const plottrLicenseLoadedSelector = checkedLicenseSelector
/**
 * @type {function(PlottrState): boolean}
 */
export const checkingLicenseSelector = createSelector(
  licenseStateSelector,
  ({ loadingLicense }) => loadingLicense
)
/**
 * @type {function(PlottrState): boolean}
 */
export const checkedProSubscriptionSelector = createSelector(
  licenseStateSelector,
  ({ proSubscriptionChecked }) => proSubscriptionChecked
)
export const proLicenseLoadedSelector = checkedProSubscriptionSelector
/**
 * @type {function(PlottrState): boolean}
 */
export const fetchedProSubscriptionSelector = createSelector(
  licenseStateSelector,
  ({ fetchedProSubscription }) => {
    return fetchedProSubscription ?? false
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const fetchingProSubscriptionSelector = createSelector(
  licenseStateSelector,
  ({ fetchingProSubscription }) => {
    return fetchingProSubscription ?? false
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const fetchedLicenseSelector = createSelector(licenseStateSelector, ({ fetchedLicense }) => {
  return fetchedLicense ?? false
})
/**
 * @type {function(PlottrState): boolean}
 */
export const fetchingLicenseSelector = createSelector(
  licenseStateSelector,
  ({ fetchingLicense }) => {
    return fetchingLicense ?? false
  }
)

/**
 * @type {function(PlottrState): SettingsWizardApplicationState}
 */
export const settingsWizardStateSelector = createSelector(
  applicationStateSelector,
  ({ settingsWizard }) => settingsWizard
)
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const currentSettingsWizardStepSelector = createSelector(
  settingsWizardStateSelector,
  ({ wizardStep }) => wizardStep
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isInSettingsWizardSelector = createSelector(
  settingsWizardStateSelector,
  ({ isInSettingsWizard }) => isInSettingsWizard
)
/**
 * @type {function(PlottrState): (Locale | Null)}
 */
export const stagedLanguageSelector = createSelector(
  settingsWizardStateSelector,
  ({ stagedLanguage }) => stagedLanguage
)
/**
 * @type {function(PlottrState): ProOnboardingApplicationState}
 */
export const proOnboardingStateSelector = createSelector(
  applicationStateSelector,
  ({ proOnboarding }) => proOnboarding
)
/**
 * @type {function(PlottrState): (number | Null)}
 */
export const currentProOnboardingStepSelector = createSelector(
  proOnboardingStateSelector,
  ({ onboardingStep }) => onboardingStep
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isOnboardingToProSelector = createSelector(
  proOnboardingStateSelector,
  ({ isOnboarding }) => isOnboarding
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isOnboardingToProFromRootSelector = createSelector(
  proOnboardingStateSelector,
  ({ isOnboardingFromRoot }) => isOnboardingFromRoot
)

/**
 * @type {function(PlottrState): boolean}
 */
export const forceCheckSelector = createSelector(
  proOnboardingStateSelector,
  ({ forceLicenseCheck }) => {
    return forceLicenseCheck
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const checkingWhatToLoadOrNeedToCheckWhatToLoadSelector = createSelector(
  checkingFileToLoadSelector,
  checkedFileToLoadSelector,
  (checkingFileToLoad, checkedFileToLoad) => {
    return checkingFileToLoad || !checkedFileToLoad
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const manipulatingAFileSelector = createSelector(
  isRenamingFileSelector,
  creatingCloudFileSelector,
  uploadingFileToCloudSelector,
  deletingFileSelector,
  savingFileAsSelector,
  (isRenamingFile, creatingCloudFile, uploadingFileToCloud, deletingFile, savingFileAs) => {
    return (
      isRenamingFile || creatingCloudFile || uploadingFileToCloud || deletingFile || savingFileAs
    )
  }
)

/**
 * @type {function(PlottrState): UpdateApplicationState}
 */
const updateStateSelector = createSelector(applicationStateSelector, ({ update }) => {
  return update
})

/**
 * @type {function(PlottrState): boolean}
 */
export const shouldCheckForUpdatesSelector = createSelector(
  updateStateSelector,
  ({ shouldCheck }) => {
    return shouldCheck
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const updateAvailableSelector = createSelector(updateStateSelector, ({ available }) => {
  return available
})

/**
 * @type {function(PlottrState): boolean}
 */
export const downloadInProgressSelector = createSelector(
  updateStateSelector,
  ({ percentDownloaded }) => {
    return percentDownloaded > 0 && percentDownloaded < 100
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const percentDownloadedSelector = createSelector(
  updateStateSelector,
  ({ percentDownloaded }) => {
    return percentDownloaded
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const finishedDownloadingSelector = createSelector(
  updateStateSelector,
  ({ percentDownloaded }) => {
    return percentDownloaded === 100
  }
)

/**
 * @type {function(PlottrState): (String | Null)}
 */
export const updateErrorSelector = createSelector(updateStateSelector, ({ error }) => {
  return error
})

/**
 * @type {function(PlottrState): UpdateInfo | Null}
 */
export const updateInfoSelector = createSelector(updateStateSelector, ({ info }) => {
  return info
})

/**
 * @type {function(PlottrState): boolean}
 */
export const updateNotificationHiddenSelector = createSelector(
  updateStateSelector,
  ({ notifierHidden }) => {
    return notifierHidden
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const checkingForUpdatesSelector = createSelector(updateStateSelector, ({ checking }) => {
  return checking
})

/**
 * @type {function(PlottrState): WorkApplicationState}
 */
const workSelector = createSelector(applicationStateSelector, ({ work }) => {
  return work
})

/**
 * @type {function(PlottrState): boolean}
 */
export const busyWithWorkThatPreventsQuittingSelector = createSelector(workSelector, ({ busy }) => {
  return busy
})

/**
 * @type {function(PlottrState): UserInteractionsApplicationState}
 */
export const userInteractionsSelector = createSelector(
  applicationStateSelector,
  ({ userInteractions }) => {
    return userInteractions || {}
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const jumpCounterSelector = createSelector(userInteractionsSelector, ({ jumpCounter }) => {
  return jumpCounter || 0
})

/**
 * @type {function(PlottrState): ('EDITING' | 'VIEWING' | 'SEARCHING')}
 */
export const editStateSelector = createSelector(userInteractionsSelector, ({ editState }) => {
  return editState || EDITING
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isSearchingSelector = createSelector(editStateSelector, (editState) => {
  return editState === SEARCHING
})

/**
 * @type {function(PlottrState): DashboardApplicationState}
 */
export const dashboardAppStateSelector = createSelector(
  applicationStateSelector,
  ({ dashboard }) => {
    return dashboard ?? {}
  }
)
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const dashboardViewToOpenToSelector = createSelector(
  dashboardAppStateSelector,
  ({ openTo }) => {
    return openTo ?? null
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const dashboardClosedSelector = createSelector(dashboardAppStateSelector, ({ closed }) => {
  return closed ?? false
})

/**
 * @type {function(PlottrState): BootingApplicationState}
 */
export const bootingAppStateSelector = createSelector(applicationStateSelector, ({ booting }) => {
  return booting ?? {}
})

/**
 * @type {function(PlottrState): boolean}
 */
export const receivedWindowStateSelector = createSelector(
  bootingAppStateSelector,
  ({ receivedWindowState }) => {
    return receivedWindowState
  }
)

/**
 * @type {function(PlottrState): boolean}
 */
export const firstTimeBootingSelector = createSelector(
  bootingAppStateSelector,
  ({ firstTimeBooting }) => {
    return firstTimeBooting ?? false
  }
)

/**
 * @type {function(PlottrState): ShutdownApplicationState}
 */
export const shutdownAppStateSelector = createSelector(applicationStateSelector, ({ shutdown }) => {
  return shutdown ?? {}
})

/**
 * @type {function(PlottrState): boolean}
 */
export const isQuittingSelector = createSelector(shutdownAppStateSelector, ({ isQuitting }) => {
  return isQuitting ?? false
})

export const displayStateSelector = createSelector(applicationStateSelector, ({ displayState }) => {
  return displayState
})

/**
 * @type {function(PlottrState): String}
 */
export const loadingStateSelector = createSelector(
  bootingAppStateSelector,
  ({ descriptionOfBootingPhase }) => {
    return descriptionOfBootingPhase
  }
)

/**
 * @type {function(PlottrState): number}
 */
export const loadingProgressSelector = createSelector(
  bootingAppStateSelector,
  ({ percentageBooted }) => {
    return percentageBooted
  }
)
